import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import articlesData from './articlesData';

const Article = () => {
  const { id } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    function isGoogleTranslated() {
      const html = document.documentElement;
      const body = document.body;

      return (
        html.classList.contains('translated-ltr') ||
        html.classList.contains('translated-rtl') ||
        body.classList.contains('translated-ltr') ||
        body.classList.contains('translated-rtl') ||
        document.cookie.indexOf('googtrans=') !== -1 ||
        html.getAttribute('lang') !== 'en'
      );
    }

    function fixValue(text) {
      if (!text) return text;

      return text
        .replace(/\bVadim Larikov's\b/g, "Vadym Larikov's")
        .replace(/\bVADIM LARIKOV'S\b/g, "VADYM LARIKOV'S")
        .replace(/\bVadim Larikovs\b/g, "Vadym Larikov's")
        .replace(/\bVADIM LARIKOVS\b/g, "VADYM LARIKOV'S")
        .replace(/\bVadim Larrykov's\b/g, "Vadym Larikov's")
        .replace(/\bVADIM LARRYKOV'S\b/g, "VADYM LARIKOV'S")
        .replace(/\bVadim Larrykov\b/g, 'Vadym Larikov')
        .replace(/\bVADIM LARRYKOV\b/g, 'VADYM LARIKOV')
        .replace(/\bVadim Larikov\b/g, 'Vadym Larikov')
        .replace(/\bVADIM LARIKOV\b/g, 'VADYM LARIKOV')
        .replace(/\bVadim's\b/g, "Vadym's")
        .replace(/\bVADIM'S\b/g, "VADYM'S")
        .replace(/\bVadim\b/g, 'Vadym')
        .replace(/\bVADIM\b/g, 'VADYM');
    }

    function shouldSkipNode(parent) {
      if (!parent || !parent.tagName) return true;

      const tag = parent.tagName.toUpperCase();

      return (
        tag === 'SCRIPT' ||
        tag === 'STYLE' ||
        tag === 'NOSCRIPT' ||
        tag === 'TEXTAREA' ||
        tag === 'CODE' ||
        tag === 'PRE'
      );
    }

    function fixTextNodes(root) {
      if (!root) return;

      const walker = document.createTreeWalker(
        root,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode(node) {
            if (!node || !node.nodeValue || !node.nodeValue.trim()) {
              return NodeFilter.FILTER_REJECT;
            }

            if (shouldSkipNode(node.parentNode)) {
              return NodeFilter.FILTER_REJECT;
            }

            if (
              node.nodeValue.indexOf('Vadim') === -1 &&
              node.nodeValue.indexOf('VADIM') === -1
            ) {
              return NodeFilter.FILTER_REJECT;
            }

            return NodeFilter.FILTER_ACCEPT;
          },
        }
      );

      const nodes = [];
      let current;

      while ((current = walker.nextNode())) {
        nodes.push(current);
      }

      nodes.forEach((node) => {
        const fixed = fixValue(node.nodeValue);
        if (fixed !== node.nodeValue) {
          node.nodeValue = fixed;
        }
      });
    }

    function fixAttributes() {
      const selectors = [
        'title',
        'meta[name="description"]',
        'meta[name="keywords"]',
        'meta[property="og:title"]',
        'meta[property="og:description"]',
        'meta[name="twitter:title"]',
        'meta[name="twitter:description"]',
        'img[alt]',
      ];

      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => {
          if (el.tagName.toLowerCase() === 'title') {
            const fixedText = fixValue(el.textContent);
            if (fixedText !== el.textContent) {
              el.textContent = fixedText;
            }
          } else if (el.tagName.toLowerCase() === 'img') {
            const alt = el.getAttribute('alt');
            const fixedAlt = fixValue(alt);
            if (fixedAlt !== alt) {
              el.setAttribute('alt', fixedAlt);
            }
          } else {
            const content = el.getAttribute('content');
            const fixedContent = fixValue(content);
            if (fixedContent !== content) {
              el.setAttribute('content', fixedContent);
            }
          }
        });
      });

      if (document.title) {
        const fixedTitle = fixValue(document.title);
        if (fixedTitle !== document.title) {
          document.title = fixedTitle;
        }
      }
    }

    function runFixes() {
      if (!isGoogleTranslated()) return;

      fixTextNodes(document.body);
      fixAttributes();
    }

    let started = false;
    let mainObserver = null;
    let pollInterval = null;
    let translationWatcher = null;

    function startAfterTranslate() {
      if (started) return;
      started = true;

      runFixes();

      mainObserver = new MutationObserver(() => {
        runFixes();
      });

      if (document.body) {
        mainObserver.observe(document.body, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      let count = 0;
      pollInterval = setInterval(() => {
        runFixes();
        count += 1;

        if (count > 180) {
          clearInterval(pollInterval);
        }
      }, 1000);
    }

    function waitForTranslate() {
      if (isGoogleTranslated()) {
        startAfterTranslate();
        return;
      }

      translationWatcher = new MutationObserver(() => {
        if (isGoogleTranslated()) {
          startAfterTranslate();
        }
      });

      translationWatcher.observe(document.documentElement, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['class', 'lang'],
      });

      const translateCheckInterval = setInterval(() => {
        if (isGoogleTranslated()) {
          startAfterTranslate();
          clearInterval(translateCheckInterval);
        }
      }, 1000);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', waitForTranslate);
    } else {
      waitForTranslate();
    }

    return () => {
      if (mainObserver) mainObserver.disconnect();
      if (translationWatcher) translationWatcher.disconnect();
      if (pollInterval) clearInterval(pollInterval);
    };
  }, []);

  const article = articlesData.find((item) => item.id === parseInt(id, 10));

  if (!article) {
    return <div className="article-not-found">Article not found</div>;
  }

  const siteUrl = 'https://yoursite.com';

  const articleUrl = article.seoSlug
    ? `${siteUrl}/articles/${article.seoSlug}`
    : `${siteUrl}/articles/${article.id}`;

  const imageUrl =
    typeof article.image === 'string'
      ? `${siteUrl}${article.image.startsWith('/') ? article.image : `/${article.image}`}`
      : `${siteUrl}/preview.png`;

  const description =
    article.seoDescription || article.text || 'Article by Global Marketing Awards';

  const title = article.seoTitle || `${article.title} | Global Marketing Awards`;
  const keywords = article.seoKeywords || `${article.title}, Global Marketing Awards`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description,
    image: [imageUrl],
    author: {
      '@type': 'Person',
      name: article.autor,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Global Marketing Awards',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    datePublished: article.date,
  };

  const renderContent = (content) => {
    return content.map((block, index) => {
      switch (block.type) {
        case 'paragraph':
          return (
            <p key={index} className="article-paragraph">
              {block.text}
            </p>
          );

        case 'header':
          return (
            <h2 key={index} className="article-header">
              {block.text}
            </h2>
          );

        case 'sub-header':
          return (
            <h3 key={index} className="article-sub-header">
              {block.text}
            </h3>
          );

        case 'image':
          return (
            <img
              key={index}
              src={block.src}
              alt={block.alt}
              className="article-page-image article-page-image--inner"
            />
          );

        case 'list':
          return (
            <ul key={index} className="article-list">
              <li>{block.text}</li>
            </ul>
          );

        default:
          return null;
      }
    });
  };

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={articleUrl} />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={articleUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:site_name" content="Global Marketing Awards" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />

        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </Helmet>

      <section className="article-hero">
        <div className="article-hero__overlay"></div>
        <div className="article-hero__content">
          <h1>Статьи</h1>
        </div>
      </section>

      <section className="article-page-section">
        <div className="article-container">
          <article className="article-page">
            <h1 className="article-page-title">{article.title}</h1>

            <div className="article-meta">
              <span className="article-date">{article.date}</span>
            </div>

            <img
              src={article.image}
              alt={article.imageAlt || article.title}
              className="article-page-image"
            />

            <div className="article-page-content">
              {renderContent(article.content)}
            </div>

            <div className="article-autor">{article.autor}</div>
          </article>
        </div>
      </section>
    </>
  );
};

export default Article;